<template>
  <div class="space-y-4">
    <p v-if="loading" class="text-sm text-[var(--plugin-gray-500)]">
      {{ t('plugin.loading') }}
    </p>
    <p v-else-if="error" class="text-sm text-red-600">
      {{ error }}
    </p>
    <template v-else>
      <div
        v-if="!products.length"
        class="flex flex-col items-center justify-center gap-3 py-8 text-center text-[var(--plugin-gray-500)]"
      >
        <svg
          class="h-12 w-12 text-[var(--plugin-gray-400)]"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M3.75 4.5h16.5m-16.5 0v14.25A2.25 2.25 0 006 21h12a2.25 2.25 0 002.25-2.25V4.5m-16.5 0L7.5 3h9l3.75 1.5M9 14.25h6m-6-3h2.25"
          />
        </svg>
        <p class="text-base font-medium text-[var(--plugin-charcoal)]">
          {{ t('plugin.productsStep.noProducts') }}
        </p>
        <p class="max-w-xs text-sm">
          {{ t('plugin.productsStep.noProductsDescription') }}
        </p>
      </div>

      <template v-else>
        <section v-if="packages.length" class="space-y-3">
          <h3 class="text-center text-base font-medium text-[var(--plugin-charcoal)]">
            {{ t('plugin.productsStep.packages') }}
          </h3>
          <ul class="flex flex-col gap-3">
            <li v-for="p in packages" :key="p.id">
              <button
                type="button"
                class="block w-full text-left"
                @click="select(p)"
              >
                <div
                  class="flex items-center gap-3 rounded-[8px] bg-[var(--plugin-gray-100)] py-3 pl-3 pr-3 transition sm:gap-4 sm:pl-[1.5rem] sm:pr-[1.5rem]"
                  :class="
                    isProductSelected(p) ? 'ring-2 ring-primary' : ''
                  "
                >
                  <div
                    class="flex h-[50px] w-[50px] shrink-0 items-center justify-center overflow-hidden rounded-[8px] bg-card"
                  >
                    <img
                      v-if="p.image_url"
                      :src="p.image_url"
                      :alt="p.name"
                      class="h-full w-full object-cover"
                    />
                    <svg
                      v-else
                      class="h-6 w-6 shrink-0 text-[var(--plugin-gray-400)]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="1.5"
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M13.625 11.0871C13.9078 10.1188 14.0491 9.63469 14.1864 9.43636C14.7773 8.58268 16.0377 8.58268 16.6285 9.43636C16.7658 9.63469 16.9072 10.1188 17.1899 11.0871C17.3009 11.467 17.3564 11.657 17.4306 11.8281C17.7306 12.5198 18.2816 13.0716 18.9723 13.372C19.1432 13.4464 19.3329 13.5019 19.7123 13.613C20.6792 13.8962 21.1626 14.0378 21.3606 14.1752C22.2131 14.767 22.2131 16.0291 21.3606 16.6209C21.1626 16.7583 20.6792 16.8999 19.7123 17.1831C19.3329 17.2942 19.1432 17.3497 18.9723 17.4241C18.2816 17.7245 17.7306 18.2763 17.4306 18.968C17.3564 19.1391 17.3009 19.3291 17.1899 19.709C16.9072 20.6773 16.7658 21.1614 16.6285 21.3597C16.0377 22.2134 14.7773 22.2134 14.1864 21.3597C14.0491 21.1614 13.9078 20.6773 13.625 19.709C13.514 19.3291 13.4586 19.1391 13.3844 18.968C13.0844 18.2763 12.5333 17.7245 11.8426 17.4241C11.6718 17.3497 11.4821 17.2942 11.1026 17.1831C10.1358 16.8999 9.65233 16.7583 9.45429 16.6209C8.60182 16.0291 8.60182 14.767 9.45429 14.1752C9.65233 14.0378 10.1358 13.8962 11.1026 13.613C11.4821 13.5019 11.6718 13.4464 11.8426 13.372C12.5333 13.0716 13.0844 12.5198 13.3844 11.8281C13.4586 11.657 13.514 11.467 13.625 11.0871Z" />
                      <path d="M5.63799 2.57971C5.68959 2.40303 5.71539 2.31468 5.73484 2.27544C5.91687 1.90819 6.43999 1.90819 6.62202 2.27544C6.64147 2.31468 6.66727 2.40303 6.71887 2.57971C6.79991 2.85725 6.84044 2.99601 6.88747 3.12379C7.28858 4.2136 8.14642 5.07266 9.23467 5.47435C9.36227 5.52144 9.50083 5.56202 9.77797 5.64319C9.9544 5.69486 10.0426 5.7207 10.0818 5.74017C10.4485 5.92246 10.4485 6.44633 10.0818 6.62862C10.0426 6.6481 9.9544 6.67394 9.77797 6.72561C9.50083 6.80677 9.36227 6.84735 9.23467 6.89445C8.14642 7.29614 7.28858 8.1552 6.88747 9.24501C6.84044 9.37278 6.79991 9.51155 6.71887 9.78908C6.66727 9.96577 6.64147 10.0541 6.62202 10.0934C6.43999 10.4606 5.91687 10.4606 5.73484 10.0934C5.71539 10.0541 5.68959 9.96577 5.63799 9.78908C5.55694 9.51155 5.51642 9.37278 5.46939 9.24501C5.06828 8.1552 4.21044 7.29614 3.12218 6.89445C2.99459 6.84735 2.85602 6.80677 2.57889 6.72561C2.40245 6.67394 2.31423 6.6481 2.27505 6.62862C1.90832 6.44633 1.90832 5.92246 2.27505 5.74017C2.31423 5.7207 2.40245 5.69486 2.57889 5.64319C2.85602 5.56202 2.99459 5.52144 3.12218 5.47435C4.21044 5.07266 5.06828 4.2136 5.46939 3.12379C5.51642 2.99601 5.55694 2.85725 5.63799 2.57971Z" />
                    </svg>
                  </div>
                  <div class="min-w-0 flex-1">
                    <div class="truncate text-sm font-normal text-[var(--plugin-charcoal)]">
                      {{ p.name }}
                    </div>
                    <div class="mt-1 text-xs text-[var(--plugin-gray-500)]">
                      {{ formatDuration(getDuration(p)) || '—' }}
                    </div>
                  </div>
                  <div class="ml-4 flex items-center gap-4">
                    <span
                      v-if="!isZlavaDna && p.price != null"
                      class="text-sm font-medium text-[var(--plugin-charcoal)]"
                    >
                      {{ formatPrice(p.price) }} €
                    </span>
                    <div
                      class="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px]"
                      :class="
                        isProductSelected(p)
                          ? 'bg-primary text-white'
                          : 'bg-card text-primary'
                      "
                    >
                      <span class="text-base font-medium leading-none">
                        {{ isProductSelected(p) ? '−' : '+' }}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            </li>
          </ul>
        </section>

        <section v-if="services.length" class="space-y-3">
          <h3 class="text-center text-base font-medium text-[var(--plugin-charcoal)]">
            {{ t('plugin.productsStep.services') }}
          </h3>
          <ul class="flex flex-col gap-3">
            <li v-for="p in services" :key="p.id">
              <button
                type="button"
                class="block w-full text-left"
                @click="select(p)"
              >
                <div
                  class="flex items-center gap-3 rounded-[8px] bg-[var(--plugin-gray-100)] py-3 pl-3 pr-3 transition sm:gap-4 sm:pl-[1.5rem] sm:pr-[1.5rem]"
                  :class="
                    isProductSelected(p) ? 'ring-2 ring-primary' : ''
                  "
                >
                  <div
                    class="flex h-[50px] w-[50px] shrink-0 items-center justify-center overflow-hidden rounded-[8px] bg-card"
                  >
                    <img
                      v-if="p.image_url"
                      :src="p.image_url"
                      :alt="p.name"
                      class="h-full w-full object-cover"
                    />
                    <svg
                      v-else
                      class="h-6 w-6 shrink-0 text-[var(--plugin-gray-400)]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="1.5"
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M13.625 11.0871C13.9078 10.1188 14.0491 9.63469 14.1864 9.43636C14.7773 8.58268 16.0377 8.58268 16.6285 9.43636C16.7658 9.63469 16.9072 10.1188 17.1899 11.0871C17.3009 11.467 17.3564 11.657 17.4306 11.8281C17.7306 12.5198 18.2816 13.0716 18.9723 13.372C19.1432 13.4464 19.3329 13.5019 19.7123 13.613C20.6792 13.8962 21.1626 14.0378 21.3606 14.1752C22.2131 14.767 22.2131 16.0291 21.3606 16.6209C21.1626 16.7583 20.6792 16.8999 19.7123 17.1831C19.3329 17.2942 19.1432 17.3497 18.9723 17.4241C18.2816 17.7245 17.7306 18.2763 17.4306 18.968C17.3564 19.1391 17.3009 19.3291 17.1899 19.709C16.9072 20.6773 16.7658 21.1614 16.6285 21.3597C16.0377 22.2134 14.7773 22.2134 14.1864 21.3597C14.0491 21.1614 13.9078 20.6773 13.625 19.709C13.514 19.3291 13.4586 19.1391 13.3844 18.968C13.0844 18.2763 12.5333 17.7245 11.8426 17.4241C11.6718 17.3497 11.4821 17.2942 11.1026 17.1831C10.1358 16.8999 9.65233 16.7583 9.45429 16.6209C8.60182 16.0291 8.60182 14.767 9.45429 14.1752C9.65233 14.0378 10.1358 13.8962 11.1026 13.613C11.4821 13.5019 11.6718 13.4464 11.8426 13.372C12.5333 13.0716 13.0844 12.5198 13.3844 11.8281C13.4586 11.657 13.514 11.467 13.625 11.0871Z" />
                      <path d="M5.63799 2.57971C5.68959 2.40303 5.71539 2.31468 5.73484 2.27544C5.91687 1.90819 6.43999 1.90819 6.62202 2.27544C6.64147 2.31468 6.66727 2.40303 6.71887 2.57971C6.79991 2.85725 6.84044 2.99601 6.88747 3.12379C7.28858 4.2136 8.14642 5.07266 9.23467 5.47435C9.36227 5.52144 9.50083 5.56202 9.77797 5.64319C9.9544 5.69486 10.0426 5.7207 10.0818 5.74017C10.4485 5.92246 10.4485 6.44633 10.0818 6.62862C10.0426 6.6481 9.9544 6.67394 9.77797 6.72561C9.50083 6.80677 9.36227 6.84735 9.23467 6.89445C8.14642 7.29614 7.28858 8.1552 6.88747 9.24501C6.84044 9.37278 6.79991 9.51155 6.71887 9.78908C6.66727 9.96577 6.64147 10.0541 6.62202 10.0934C6.43999 10.4606 5.91687 10.4606 5.73484 10.0934C5.71539 10.0541 5.68959 9.96577 5.63799 9.78908C5.55694 9.51155 5.51642 9.37278 5.46939 9.24501C5.06828 8.1552 4.21044 7.29614 3.12218 6.89445C2.99459 6.84735 2.85602 6.80677 2.57889 6.72561C2.40245 6.67394 2.31423 6.6481 2.27505 6.62862C1.90832 6.44633 1.90832 5.92246 2.27505 5.74017C2.31423 5.7207 2.40245 5.69486 2.57889 5.64319C2.85602 5.56202 2.99459 5.52144 3.12218 5.47435C4.21044 5.07266 5.06828 4.2136 5.46939 3.12379C5.51642 2.99601 5.55694 2.85725 5.63799 2.57971Z" />
                    </svg>
                  </div>
                  <div class="min-w-0 flex-1">
                    <div class="truncate text-sm font-normal text-[var(--plugin-charcoal)]">
                      {{ p.name }}
                    </div>
                    <div class="mt-1 text-xs text-[var(--plugin-gray-500)]">
                      {{ formatDuration(getDuration(p)) || '—' }}
                    </div>
                  </div>
                  <div class="ml-4 flex items-center gap-4">
                    <span
                      v-if="!isZlavaDna && p.price != null"
                      class="text-sm font-medium text-[var(--plugin-charcoal)]"
                    >
                      {{ formatPrice(p.price) }} €
                    </span>
                    <div
                      class="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px]"
                      :class="
                        isProductSelected(p)
                          ? 'bg-primary text-white'
                          : 'bg-card text-primary'
                      "
                    >
                      <span class="text-base font-medium leading-none">
                        {{ isProductSelected(p) ? '−' : '+' }}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            </li>
          </ul>
        </section>

        <section v-if="sortedWorkshops.length" class="space-y-3">
          <h3 class="text-center text-base font-medium text-[var(--plugin-charcoal)]">
            {{ t('plugin.productsStep.workshops') }}
          </h3>
          <ul class="flex flex-col gap-3">
            <li v-for="p in sortedWorkshops" :key="p.id">
              <button
                type="button"
                class="block w-full text-left"
                :disabled="isWorkshopFullyBooked(p)"
                :class="isWorkshopFullyBooked(p) ? 'cursor-not-allowed opacity-75' : ''"
                @click="select(p)"
              >
                <div
                  class="flex items-center gap-3 rounded-[8px] bg-[var(--plugin-gray-100)] py-3 pl-3 pr-3 transition sm:gap-4 sm:pl-[1.5rem] sm:pr-[1.5rem]"
                  :class="[
                    isProductSelected(p) && !isWorkshopFullyBooked(p) ? 'ring-2 ring-primary' : '',
                    isWorkshopFullyBooked(p) ? 'opacity-90' : '',
                  ]"
                >
                  <div
                    class="flex h-[50px] w-[50px] shrink-0 items-center justify-center overflow-hidden rounded-[8px] bg-card"
                  >
                    <img
                      v-if="p.image_url"
                      :src="p.image_url"
                      :alt="p.name"
                      class="h-full w-full object-cover"
                    />
                    <svg
                      v-else
                      class="h-6 w-6 shrink-0 text-[var(--plugin-gray-400)]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="1.5"
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M13.625 11.0871C13.9078 10.1188 14.0491 9.63469 14.1864 9.43636C14.7773 8.58268 16.0377 8.58268 16.6285 9.43636C16.7658 9.63469 16.9072 10.1188 17.1899 11.0871C17.3009 11.467 17.3564 11.657 17.4306 11.8281C17.7306 12.5198 18.2816 13.0716 18.9723 13.372C19.1432 13.4464 19.3329 13.5019 19.7123 13.613C20.6792 13.8962 21.1626 14.0378 21.3606 14.1752C22.2131 14.767 22.2131 16.0291 21.3606 16.6209C21.1626 16.7583 20.6792 16.8999 19.7123 17.1831C19.3329 17.2942 19.1432 17.3497 18.9723 17.4241C18.2816 17.7245 17.7306 18.2763 17.4306 18.968C17.3564 19.1391 17.3009 19.3291 17.1899 19.709C16.9072 20.6773 16.7658 21.1614 16.6285 21.3597C16.0377 22.2134 14.7773 22.2134 14.1864 21.3597C14.0491 21.1614 13.9078 20.6773 13.625 19.709C13.514 19.3291 13.4586 19.1391 13.3844 18.968C13.0844 18.2763 12.5333 17.7245 11.8426 17.4241C11.6718 17.3497 11.4821 17.2942 11.1026 17.1831C10.1358 16.8999 9.65233 16.7583 9.45429 16.6209C8.60182 16.0291 8.60182 14.767 9.45429 14.1752C9.65233 14.0378 10.1358 13.8962 11.1026 13.613C11.4821 13.5019 11.6718 13.4464 11.8426 13.372C12.5333 13.0716 13.0844 12.5198 13.3844 11.8281C13.4586 11.657 13.514 11.467 13.625 11.0871Z" />
                      <path d="M5.63799 2.57971C5.68959 2.40303 5.71539 2.31468 5.73484 2.27544C5.91687 1.90819 6.43999 1.90819 6.62202 2.27544C6.64147 2.31468 6.66727 2.40303 6.71887 2.57971C6.79991 2.85725 6.84044 2.99601 6.88747 3.12379C7.28858 4.2136 8.14642 5.07266 9.23467 5.47435C9.36227 5.52144 9.50083 5.56202 9.77797 5.64319C9.9544 5.69486 10.0426 5.7207 10.0818 5.74017C10.4485 5.92246 10.4485 6.44633 10.0818 6.62862C10.0426 6.6481 9.9544 6.67394 9.77797 6.72561C9.50083 6.80677 9.36227 6.84735 9.23467 6.89445C8.14642 7.29614 7.28858 8.1552 6.88747 9.24501C6.84044 9.37278 6.79991 9.51155 6.71887 9.78908C6.66727 9.96577 6.64147 10.0541 6.62202 10.0934C6.43999 10.4606 5.91687 10.4606 5.73484 10.0934C5.71539 10.0541 5.68959 9.96577 5.63799 9.78908C5.55694 9.51155 5.51642 9.37278 5.46939 9.24501C5.06828 8.1552 4.21044 7.29614 3.12218 6.89445C2.99459 6.84735 2.85602 6.80677 2.57889 6.72561C2.40245 6.67394 2.31423 6.6481 2.27505 6.62862C1.90832 6.44633 1.90832 5.92246 2.27505 5.74017C2.31423 5.7207 2.40245 5.69486 2.57889 5.64319C2.85602 5.56202 2.99459 5.52144 3.12218 5.47435C4.21044 5.07266 5.06828 4.2136 5.46939 3.12379C5.51642 2.99601 5.55694 2.85725 5.63799 2.57971Z" />
                    </svg>
                  </div>
                  <div class="min-w-0 flex-1">
                    <div class="truncate text-sm font-normal text-[var(--plugin-charcoal)]">
                      {{ p.name }}
                    </div>
                    <div class="mt-1 text-xs text-[var(--plugin-gray-500)]">
                      {{ formatDuration(getDuration(p)) || '—' }}
                    </div>
                    <div
                      v-if="p.workshop_datetime"
                      class="mt-1 text-xs text-[var(--plugin-gray-500)]"
                    >
                      {{ formatDate(p.workshop_datetime) }}
                    </div>
                    <span
                      v-if="isWorkshopFullyBooked(p)"
                      class="mt-1.5 inline-block rounded bg-[var(--plugin-gray-400)] px-2 py-0.5 text-xs font-medium text-white"
                    >
                      {{ t('plugin.productsStep.fullyBooked') }}
                    </span>
                  </div>
                  <div class="ml-4 flex items-center gap-4">
                    <div class="flex flex-col items-end">
                      <span
                        v-if="!isZlavaDna && p.price != null"
                        class="text-sm font-medium text-[var(--plugin-charcoal)]"
                      >
                        {{ formatPrice(p.price) }} €
                      </span>
                      <span
                        v-if="workshopCapacityText(p)"
                        class="mt-1 text-xs text-[var(--plugin-gray-500)]"
                      >
                        {{ workshopCapacityText(p) }}
                      </span>
                    </div>
                    <div
                      v-if="!isWorkshopFullyBooked(p)"
                      class="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px]"
                      :class="
                        isProductSelected(p)
                          ? 'bg-primary text-white'
                          : 'bg-card text-primary'
                      "
                    >
                      <span class="text-base font-medium leading-none">
                        {{ isProductSelected(p) ? '−' : '+' }}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            </li>
          </ul>
        </section>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { getServicesPackages } from '@/api/public'
import type { PublicReservationProduct } from '@/types/api'
import { useWizardState, setProduct } from '@/composables/useWizardState'
import { t } from '@/i18n'

const props = defineProps<{ branchId: number; source?: string }>()
const state = useWizardState()
const products = ref<PublicReservationProduct[]>([])
const loading = ref(true)
const error = ref<string | null>(null)

const isZlavaDna = computed(() => props.source === 'zlavadna')

const isWorkshop = (p: PublicReservationProduct) =>
  p.type === 'workshop' || p.is_workshop === true

function isWorkshopFullyBooked(p: PublicReservationProduct): boolean {
  if (!isWorkshop(p)) return false
  if (p.available !== undefined && p.available !== null) return p.available <= 0
  if (p.capacity != null && p.booked != null) return p.booked >= p.capacity
  return false
}

function workshopCapacityText(p: PublicReservationProduct): string | null {
  if (!isWorkshop(p) || p.capacity == null) return null
  const booked =
    p.booked ??
    (p.available != null ? Math.max(0, p.capacity - p.available) : 0)
  return `${booked}/${p.capacity}`
}

const packages = computed(() =>
  products.value.filter((p) => p.type === 'package' && !isWorkshop(p))
)

const services = computed(() =>
  products.value.filter((p) => p.type === 'service' && !isWorkshop(p))
)

const workshops = computed(() =>
  products.value.filter((p) => isWorkshop(p))
)

const sortedWorkshops = computed(() => {
  const list = [...workshops.value]
  return list.sort((a, b) => {
    const aDate = a.workshop_datetime ? new Date(a.workshop_datetime) : null
    const bDate = b.workshop_datetime ? new Date(b.workshop_datetime) : null
    if (!aDate && !bDate) return 0
    if (!aDate) return 1
    if (!bDate) return -1
    return aDate.getTime() - bDate.getTime()
  })
})

function isProductSelected(p: PublicReservationProduct): boolean {
  return (
    state.product != null &&
    state.product.id === p.id &&
    state.product.type === p.type
  )
}

function select(p: PublicReservationProduct) {
  if (isWorkshopFullyBooked(p)) return
  if (isProductSelected(p)) {
    setProduct(null)
  } else {
    setProduct(p)
  }
}

function formatDate(iso: string) {
  try {
    const d = new Date(iso)
    const day = d.getDate()
    const month = d.getMonth() + 1
    const year = d.getFullYear()
    const hours = String(d.getHours()).padStart(2, '0')
    const minutes = String(d.getMinutes()).padStart(2, '0')
    return `${day}.${month}.${year} ${hours}:${minutes}`
  } catch {
    return iso
  }
}

function getDuration(p: PublicReservationProduct): number | undefined {
  return p.duration_minutes ?? p.duration
}

function formatDuration(minutes?: number) {
  if (minutes == null || minutes < 0) return ''
  if (minutes === 0) return '0 min'
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (hours && rest) {
    return `${hours} h ${rest} min`
  }
  if (hours) {
    return `${hours} h`
  }
  return `${rest} min`
}

function formatPrice(price: number): string {
  return Number(price).toFixed(2)
}

watch(
  () => [props.branchId, props.source] as const,
  async ([branchId, source]) => {
    if (!branchId) return
    loading.value = true
    error.value = null
    try {
      products.value = await getServicesPackages(branchId, source)
    } catch (e) {
      error.value = e instanceof Error ? e.message : t('plugin.error.load')
    } finally {
      loading.value = false
    }
  },
  { immediate: true }
)
</script>
